
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Package, Receipt, Download, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { PurchaseReceiptDialog } from "@/components/purchases/PurchaseReceiptDialog";
import { AppointmentReceiptDialog } from "@/components/appointments/AppointmentReceiptDialog";

// Types pour les données de reçu
interface PurchaseReceiptData {
  product_title?: string;
  total_amount?: number;
  [key: string]: any;
}

interface AppointmentReceiptData {
  service_name?: string;
  service_price?: number;
  [key: string]: any;
}

export const UserRequestsSection = () => {
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedPurchaseRequestId, setSelectedPurchaseRequestId] = useState<string | null>(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  // Fetch purchase requests
  const { data: purchaseRequests = [] } = useQuery({
    queryKey: ["user-purchase-requests"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("product_purchase_requests")
        .select(`
          *,
          professional_products!product_id (title, price),
          profiles!professional_id (full_name)
        `)
        .eq("buyer_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  // Fetch appointment requests
  const { data: appointmentRequests = [] } = useQuery({
    queryKey: ["user-appointment-requests"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("professional_appointments")
        .select(`
          *,
          professional_services!service_id (service_name, price),
          profiles!professional_id (full_name)
        `)
        .eq("patient_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  // Fetch purchase receipts
  const { data: purchaseReceipts = [] } = useQuery({
    queryKey: ["user-purchase-receipts"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("purchase_request_receipts")
        .select("*")
        .eq("buyer_id", user.id)
        .order("generated_at", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  // Fetch appointment receipts
  const { data: appointmentReceipts = [] } = useQuery({
    queryKey: ["user-appointment-receipts"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("appointment_receipts")
        .select("*")
        .eq("patient_id", user.id)
        .order("generated_at", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const getStatusBadge = (status: string, type: 'purchase' | 'appointment') => {
    const statusConfig = {
      purchase: {
        pending: { label: "En attente", variant: "secondary" as const, icon: Clock },
        approved: { label: "Approuvé", variant: "default" as const, icon: CheckCircle },
        rejected: { label: "Refusé", variant: "destructive" as const, icon: XCircle }
      },
      appointment: {
        pending: { label: "En attente", variant: "secondary" as const, icon: Clock },
        confirmed: { label: "Confirmé", variant: "default" as const, icon: CheckCircle },
        cancelled: { label: "Annulé", variant: "destructive" as const, icon: XCircle },
        completed: { label: "Terminé", variant: "outline" as const, icon: CheckCircle }
      }
    };

    const config = statusConfig[type][status as keyof typeof statusConfig[typeof type]];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <IconComponent className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredPurchaseRequests = purchaseRequests.filter(request => {
    if (filterType !== "all" && filterType !== "purchase") return false;
    if (filterStatus !== "all" && request.status !== filterStatus) return false;
    return true;
  });

  const filteredAppointmentRequests = appointmentRequests.filter(request => {
    if (filterType !== "all" && filterType !== "appointment") return false;
    if (filterStatus !== "all" && request.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <Tabs defaultValue="requests" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="requests" className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Mes Demandes
          </TabsTrigger>
          <TabsTrigger value="receipts" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Mes Reçus
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Type de demande" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les demandes</SelectItem>
                <SelectItem value="purchase">Demandes d'achat</SelectItem>
                <SelectItem value="appointment">Demandes de rendez-vous</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="approved">Approuvé</SelectItem>
                <SelectItem value="confirmed">Confirmé</SelectItem>
                <SelectItem value="rejected">Refusé</SelectItem>
                <SelectItem value="cancelled">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Purchase Requests */}
          {(filterType === "all" || filterType === "purchase") && (
            <div className="space-y-3">
              {filteredPurchaseRequests.map((request) => (
                <Card key={request.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <Package className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">
                            {request.professional_products?.title || "Produit"}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Vendeur: {request.profiles?.full_name || "Professionnel"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Quantité: {request.quantity} • Prix: {request.professional_products?.price || 0}€
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Demandé le {format(new Date(request.created_at), "PPP", { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(request.status, 'purchase')}
                        {request.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedPurchaseRequestId(request.id)}
                            className="flex items-center gap-1"
                          >
                            <Receipt className="h-3 w-3" />
                            Voir le reçu
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Appointment Requests */}
          {(filterType === "all" || filterType === "appointment") && (
            <div className="space-y-3">
              {filteredAppointmentRequests.map((request) => (
                <Card key={request.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <Calendar className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">
                            {request.professional_services?.service_name || "Consultation"}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Professionnel: {request.profiles?.full_name || "Professionnel"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Date souhaitée: {format(new Date(request.requested_date), "PPPp", { locale: fr })}
                          </p>
                          {request.professional_services?.price && (
                            <p className="text-sm text-muted-foreground">
                              Prix: {request.professional_services.price}€
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Demandé le {format(new Date(request.created_at), "PPP", { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(request.status, 'appointment')}
                        {request.status === 'confirmed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedAppointmentId(request.id)}
                            className="flex items-center gap-1"
                          >
                            <Receipt className="h-3 w-3" />
                            Voir le reçu
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {filteredPurchaseRequests.length === 0 && filteredAppointmentRequests.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Aucune demande trouvée</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="receipts" className="space-y-4">
          {/* Purchase Receipts */}
          <div className="space-y-3">
            <h3 className="font-medium text-lg">Reçus d'achat</h3>
            {purchaseReceipts.map((receipt) => {
              const receiptData = receipt.receipt_data as PurchaseReceiptData;
              return (
                <Card key={receipt.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <Receipt className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">
                            Reçu d'achat #{receipt.receipt_number}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {receiptData?.product_title || "Produit"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Montant: {receiptData?.total_amount || 0}€
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Généré le {format(new Date(receipt.generated_at), "PPP", { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setSelectedPurchaseRequestId(receipt.purchase_request_id)}
                        className="flex items-center gap-1"
                      >
                        <Download className="h-3 w-3" />
                        Télécharger
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Appointment Receipts */}
          <div className="space-y-3">
            <h3 className="font-medium text-lg">Reçus de rendez-vous</h3>
            {appointmentReceipts.map((receipt) => {
              const receiptData = receipt.receipt_data as AppointmentReceiptData;
              return (
                <Card key={receipt.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <Receipt className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">
                            Reçu de rendez-vous #{receipt.receipt_number}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {receiptData?.service_name || "Consultation"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Montant: {receiptData?.service_price || 0}€
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Généré le {format(new Date(receipt.generated_at), "PPP", { locale: fr })}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setSelectedAppointmentId(receipt.appointment_id)}
                        className="flex items-center gap-1"
                      >
                        <Download className="h-3 w-3" />
                        Télécharger
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {purchaseReceipts.length === 0 && appointmentReceipts.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Receipt className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Aucun reçu disponible</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Receipt Dialogs */}
      {selectedPurchaseRequestId && (
        <PurchaseReceiptDialog
          open={!!selectedPurchaseRequestId}
          onOpenChange={() => setSelectedPurchaseRequestId(null)}
          purchaseRequestId={selectedPurchaseRequestId}
        />
      )}

      {selectedAppointmentId && (
        <AppointmentReceiptDialog
          open={!!selectedAppointmentId}
          onOpenChange={() => setSelectedAppointmentId(null)}
          appointmentId={selectedAppointmentId}
        />
      )}
    </div>
  );
};
